// LabourChargesPrint.jsx ✅ PRINT PAGE (calls SEARCH API using querystring params)
// ✅ Reads: branchid, fromDate, toDate, projectid, supplierid
// ✅ Calls: GET /api/labourcharge/search?branchid=&fromDate=&toDate=&projectid=&supplierid=
// ✅ Renders Crystal-like print table
// ✅ Auto print

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const LabourChargesPrint = () => {
  const [reportData, setReportData] = useState({ charges: [], totals: null });
  const [loading, setLoading] = useState(true);

  // ✅ Read search params from query string
  const params = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      branchid: urlParams.get("branchid") || "",
      fromDate: urlParams.get("fromDate") || "",
      toDate: urlParams.get("toDate") || "",
      projectid: urlParams.get("projectid") || "0",
      supplierid: urlParams.get("supplierid") || "0",
    };
  }, []);

  useEffect(() => {
    const loadReport = async () => {
      try {
        if (!params.branchid || !params.fromDate || !params.toDate) {
          setReportData({ charges: [], totals: null });
          setLoading(false);
          return;
        }

        setLoading(true);

        // ✅ Call SEARCH API
        const res = await axios.get(`${BASE_URL}/api/labourcharge/search`, {
          params: {
            branchid: params.branchid,
            fromDate: params.fromDate,
            toDate: params.toDate,
            projectid: params.projectid || 0,
            supplierid: params.supplierid || 0,
          },
        });

        const data = res.data;

        if (!data?.success) {
          setReportData({ charges: [], totals: null });
          setLoading(false);
          return;
        }

        // ✅ Backend should return: { data: rows, totals: {...} }
        // rows fields expected: date, supplier, tokenNo, projectName, location, totalExpenditure, advance, otherCharges, netAmount
        const normalized = {
          charges: data.data || [],
          totals: data.totals || null,
        };

        setReportData(normalized);

        // ✅ Auto print
        setTimeout(() => window.print(), 300);
      } catch (err) {
        console.error(err);
        setReportData({ charges: [], totals: null });
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [params.branchid, params.fromDate, params.toDate, params.projectid, params.supplierid]);

  const money = (n) =>
    Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ✅ totals fallback if backend doesn't send totals
  const computedTotals = useMemo(() => {
    if (reportData?.totals) return reportData.totals;

    const t = (reportData?.charges || []).reduce(
      (acc, r) => {
        acc.totalExpenditure += Number(r.totalExpenditure || 0);
        acc.totalAdvance += Number(r.advance || 0);
        acc.totalOtherCharges += Number(r.otherCharges || 0);
        acc.totalNetAmount += Number(r.netAmount || 0);
        return acc;
      },
      { totalExpenditure: 0, totalAdvance: 0, totalOtherCharges: 0, totalNetAmount: 0 }
    );

    return t;
  }, [reportData]);

  return (
    <div className="max-w-5xl mx-auto font-sans bg-white text-xs print:p-0 print:max-w-none p-4">
      <div className="border-2 border-black">
        {/* Header */}
        <div className="px-4 py-3 border-b-2 border-black">
          <h1 className="text-xl font-bold text-red-600">Nissi Infotech</h1>
        </div>

        {/* Report Title */}
        <div className="text-center font-semibold py-2 text-sm border-b-2 border-black">
          Labour Charges Details
        </div>

        {/* Table */}
        <div className="px-0">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-white">
                <th className="border border-black px-2 py-2 font-semibold text-center">Date</th>
                <th className="border border-black px-2 py-2 font-semibold text-center">Supplier</th>
                <th className="border border-black px-2 py-2 font-semibold text-center">
                  Token<br />No
                </th>
                <th className="border border-black px-2 py-2 font-semibold text-center">
                  Project<br />Name
                </th>
                <th className="border border-black px-2 py-2 font-semibold text-center">Location</th>
                <th className="border border-black px-2 py-2 font-semibold text-center">
                  Total<br />Expenditure
                </th>
                <th className="border border-black px-2 py-2 font-semibold text-center">Advance</th>
                <th className="border border-black px-2 py-2 font-semibold text-center">
                  Other<br />charges
                </th>
                <th className="border border-black px-2 py-2 font-semibold text-center">
                  Net<br />Amount
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4 border border-black">
                    Loading...
                  </td>
                </tr>
              ) : !reportData || reportData.charges.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-4 border border-black">
                    No data found
                  </td>
                </tr>
              ) : (
                <>
                  {reportData.charges.map((charge, idx) => (
                    <tr key={idx}>
                      <td className="border border-black px-2 py-1 text-center">{charge.date || ""}</td>
                      <td className="border border-black px-2 py-1">{charge.supplier || ""}</td>
                      <td className="border border-black px-2 py-1 text-center">{charge.tokenNo || ""}</td>
                      <td className="border border-black px-2 py-1">{charge.projectName || ""}</td>
                      <td className="border border-black px-2 py-1 text-center">{charge.location || ""}</td>
                      <td className="border border-black px-2 py-1 text-right">{money(charge.totalExpenditure)}</td>
                      <td className="border border-black px-2 py-1 text-right">
                        {Number(charge.advance || 0) ? money(charge.advance) : ""}
                      </td>
                      <td className="border border-black px-2 py-1 text-right">
                        {Number(charge.otherCharges || 0) ? money(charge.otherCharges) : ""}
                      </td>
                      <td className="border border-black px-2 py-1 text-right">{money(charge.netAmount)}</td>
                    </tr>
                  ))}

                  {/* ✅ Total Row (Crystal style: show total expenditure bold) */}
                  <tr className="font-bold">
                    <td colSpan="5" className="border-none px-2 py-1"></td>
                    <td className="border-t-2 border-black px-2 py-1 text-right">
                      {money(computedTotals.totalExpenditure)}
                    </td>
                    <td colSpan="2" className="border-none"></td>
                    <td className="border-t-2 border-black px-2 py-1 text-right">
                      {money(computedTotals.totalNetAmount)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Space */}
        <div className="h-64"></div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-black mt-4">
          <div className="text-xs text-gray-600">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabourChargesPrint;
